import { Fragment, useCallback, useEffect, useState, useRef } from 'react'
import { AnimatedEmojis } from '../../../components/ANewDesign/AnimatedEmojis'
import { useStore } from '../../../models'
import { EMOJI_ANIM_DURATION } from '../../../components/ANewDesign/AnimatedEmoji'
import { playSound } from '../../../utils/audioService'
import { ChatKind, MessageContractModel, MessageContractModelType } from '../../../models/api-store'
import { randomIntFromInterval } from '@ppwr/poker'

export const useCelebration = () => {
  const {
    communication: { celebrations, clearCelebrations, handleCelebrations },
    permissions: { isSoundEnabled }
  } = useStore()

  const [emojis, setEmojis] = useState<JSX.Element[]>([])
  const lastCelebrationTimeOut = useRef<NodeJS.Timeout | undefined>(undefined)
  const playAudioForFirstTime = useRef(true)

  const clearEmojis = useCallback(() => {
    setEmojis([])
    clearCelebrations()
  }, [setEmojis, clearCelebrations])

  const triggerLocalCelebration = useCallback(
    (isUserWinner?: boolean) => {
      const celebrationMessage = createLocalCelebration(isUserWinner)
      handleCelebrations(celebrationMessage)
    },
    [handleCelebrations]
  )

  const triggerCelebrationFromRemote = useCallback(
    (emoji: string, userId: string) => {
      const celebrationMessage = createRemoteCelebration(emoji, userId)
      handleCelebrations(celebrationMessage)
    },
    [handleCelebrations]
  )

  useEffect(() => {
    if (celebrations.length === 1) {
      if (playAudioForFirstTime.current && isSoundEnabled) {
        playSound('celebration')
        playAudioForFirstTime.current = false
      }
    }

    if (celebrations.length > 0) {
      const lastCelebration = celebrations[celebrations.length - 1]

      if (lastCelebrationTimeOut.current) {
        // Don't remove celebrations yet, abort it because new celebration started.
        clearTimeout(lastCelebrationTimeOut.current)
      }

      // @ts-expect-error
      lastCelebrationTimeOut.current = setTimeout(() => {
        // if last celebration timeout is met for last celebration, let's erase all celebrations.
        console.log('timeout: Clearing all celebrations')
        clearEmojis()
      }, EMOJI_ANIM_DURATION)

      if (lastCelebration.emoji) {
        setEmojis((prev) => [
          ...prev,
          <AnimatedEmojis emoji={lastCelebration.emoji as string} key={lastCelebration.emoji} />
        ])
      }
    }

    return () => {
      playAudioForFirstTime.current = true
    }
  }, [celebrations.length, celebrations, playSound, clearEmojis])

  const renderEmojis = emojis.map((emojiElement, idx) => <Fragment key={`emojis-${idx}`}>{emojiElement}</Fragment>)

  return {
    renderEmojis,
    clearEmojis,
    count: celebrations.length,
    triggerLocalCelebration,
    triggerCelebrationFromRemote
  }
}

const suitEmojis = ['♠️', '♥️', '♦️', '♣️']
const cardEmojis = ['🂡', '🂢', '🂣']
const botEmojis = ['🤖', '👾', '🛸']
const userEmojis = ['🎉', '🏆', '👏', '💥', '🚀', '🥳', '😎']
const prefix = '🎉:'

function getRandom<T>(array: T[]): T {
  return array[randomIntFromInterval(0, array.length - 1)]
}

/**
 * Helper to format celebration message for Convex
 * Returns the message string that should be sent via Convex mutations
 */
export const formatCelebrationMessage = (emoji: string): string => {
  return `${prefix}${emoji}`
}

/**
 * Helper to detect if a message is a celebration
 * Returns true if the message is formatted as a celebration
 */
export const isCelebrationMessage = (message: string | undefined): boolean => {
  return !!message?.startsWith(prefix)
}

/**
 * Helper to parse celebration message from Convex
 * Returns the emoji if the message is a celebration, null otherwise
 */
export const parseCelebrationMessage = (message: string | undefined): string | null => {
  if (message && isCelebrationMessage(message)) {
    return message.slice(prefix.length).substring(0, 2) // Remove the prefix and return just the emoji
  }
  return null
}

/**
 * Core helper to create a celebration message
 * Used by both local and remote celebration creators
 */
const createCelebrationMessage = (
  emoji: string,
  userId: string,
  channelName?: string,
  _id?: string
): MessageContractModelType => {
  const payload = {
    emoji,
    kind: ChatKind.engine,
    userId,
    _id: _id || Date.now().toString()
  }

  const msg: MessageContractModelType = MessageContractModel.create({
    channelName,
    payload
  }) as MessageContractModelType

  return msg
}

/**
 * Creates a local celebration message (auto-picks emoji based on winner status)
 */
const createLocalCelebration = (isUserWinner?: boolean) => {
  const celebrationEmoji = isUserWinner ? getRandom(userEmojis) : getRandom(botEmojis)
  return createCelebrationMessage(celebrationEmoji, 'me')
}

/**
 * Creates a remote celebration message from a specific emoji
 * Used when receiving celebration messages from other players
 */
export const createRemoteCelebration = (
  emoji: string,
  userId: string,
  channelName?: string,
  _id?: string
): MessageContractModelType => {
  return createCelebrationMessage(emoji, userId, channelName, _id)
}
